import type { Transport } from '@fafi/shared';

// Named-leg fan-out over one underlying Transport (M2-S9). The Transport contract is single-handler
// ("last registration wins"), but the live client has two consumers of one socket: the tested
// Connection reconnect state machine and the NetGame message loop. Each takes a named leg; handler
// registration replaces within that leg (so Connection's re-attach on every resume attempt never
// accumulates handlers), and legs persist across swapUnderlying() — a resumed socket feeds the same
// consumers without re-wiring.

interface Leg {
  msg: ((bytes: Uint8Array) => void) | null;
  close: ((code: number) => void) | null;
}

export class MultiplexTransport {
  private underlying: Transport;
  private generation = 0; // D62: bumped on every swap — older sockets are ignored and closed
  private readonly legs = new Map<string, Leg>();

  constructor(initial: Transport) {
    this.underlying = initial;
    this.wire(initial);
  }

  private wire(t: Transport): void {
    // D62: every wired socket carries the generation it was wired at. A socket that has since been
    // superseded must be DEAF: previously its late close (a failed overlapping attempt, an idle-timeout
    // teardown, a server 1008) fanned out to every leg and knocked the healthy, PLAYING connection back
    // into RECONNECTING — which opened yet another socket and wiped the netcode epoch again.
    const gen = this.generation;
    t.onMessage((bytes) => {
      if (gen !== this.generation) return;
      for (const leg of this.legs.values()) leg.msg?.(bytes);
    });
    t.onClose((code) => {
      if (gen !== this.generation) return; // a superseded socket does not speak for the live one
      for (const leg of this.legs.values()) leg.close?.(code);
    });
  }

  /** Replace the underlying socket (reconnect). Existing legs keep receiving from the new socket. */
  swapUnderlying(t: Transport): void {
    const old = this.underlying;
    this.generation += 1; // everything wired before this instant goes deaf
    this.underlying = t;
    this.wire(t);
    // D62: and the replaced socket is CLOSED. Leaking it kept a live WebSocket (and, server-side, a
    // Session + an ipCounts slot) open per reconnect attempt; at 2 attempts/s the 32-per-IP concurrent
    // cap was reached in ~16 s and every later attempt was refused with 1008 — a self-sustaining
    // lockout for the rest of the match.
    try {
      old.close(1000);
    } catch {
      /* already gone */
    }
  }

  send(bytes: Uint8Array): void {
    this.underlying.send(bytes);
  }

  /** A stable per-consumer Transport view. Registration replaces this leg's handlers only. */
  leg(name: string): Transport {
    let entry = this.legs.get(name);
    if (!entry) {
      entry = { msg: null, close: null };
      this.legs.set(name, entry);
    }
    const e = entry;
    return {
      send: (bytes): void => this.send(bytes),
      onMessage: (h): void => {
        e.msg = h;
      },
      onClose: (h): void => {
        e.close = h;
      },
      close: (code): void => this.underlying.close(code),
      bufferedAmount: (): number => this.underlying.bufferedAmount?.() ?? 0,
    };
  }
}
